import { FC, useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";

import { userState } from "../../store/user";

import GlobalAppLoading from "../UI/GlobalAppLoading";

import VoteBlocked from "./VoteBlocked";
import VoteCaptcha from "./VoteCaptcha";
import RegularVote from "./RegularVote";
import SilentVote from "./SilentVote";
import VoteEmpty from "./VoteEmpty";
import { BattleModelDocument } from "../image/Image";
import { getLang, i18n } from "../../i18n";

export interface CaptchaModelDocument {
  _id: string;
  userID: string;
  select: string;
  option1: string;
  option2: string;
}

export interface TopicModelDocument {
  _id: string;
  title: string;
  question: string;
  sponsor: string;
  andTags: string[];
  orTags: string[];
  createdAt: Date;
  updatedAt: Date;
  battlesCount: number;
  artworksCount: number;
}

export interface VoteModelDocument {
  _id: string;
  userID: string;
  battleID: string;
  artwork1ID: string;
  artwork1Src: string;
  artwork2ID: string;
  artwork2Src: string;
  selectedArtworkID?: string;
  status: string;
  rewardExpertRate: number;
  prevExpertRate?: number;
  expertRate: number;
  //reward: number;
  battle?: BattleModelDocument;
  question?: string;
}

export interface SilentVoteModelDocument {
  _id: string;
  userID: string;
  artwork1ID: string;
  artwork1Src: string;
  artwork2ID: string;
  artwork2Src: string;
  selectedArtworkID?: string;
  status: string;
  question?: string;
}

const Vote: FC = () => {
  const lang = getLang();
  const t = i18n.vote[lang];
  const API_URL = process.env.REACT_APP_API_URL;
  const user = useSelector(userState);

  const [serverTime, setServerTime] = useState(0);
  const [screenIsLoading, setScreenIsLoading] = useState(true);
  const [isBlocked, setIsBlocked] = useState(false);
  const [voteTipViewed, setVoteTipViewed] = useState(true);
  const [captcha, setCaptcha] = useState<CaptchaModelDocument | null>(null);
  const [vote, setVote] = useState<VoteModelDocument | null>(null);
  const [balance, setBalance] = useState(0);
  const [silentVote, setSilentVote] = useState<SilentVoteModelDocument | null>(
    null
  );
  useEffect(() => {
    console.log("vote use effect");
    const timeoutId = setTimeout(() => {
      setScreenIsLoading(true);
      getVotePair();
    }, 60000);

    return () => clearTimeout(timeoutId);
  }, [serverTime]);
  const getVotePair = async () => {
    await axios
      .get(`${API_URL}/votes`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${user.sessionToken}` },
      })
      .then((response) => {
        console.log(
          "response.data.data.voteTipViewed",
          response.data.data.voteTipViewed
        );
        setServerTime(response.data.data.serverTime);
        setVoteTipViewed(response.data.data.voteTipViewed);
        setIsBlocked(response.data.data.isBlocked);
        setCaptcha(response.data.data.captcha);
        setVote(response.data.data.vote);
        setSilentVote(response.data.data.silentVote);
        setBalance(response.data.data.balance);
        setScreenIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setScreenIsLoading(false);
      });
  };
  const voteCaptchaHandler = async (color: string) => {
    setScreenIsLoading(true);
    await axios
      .post(
        `${API_URL}/votes/captcha/${color}`,
        {},
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${user.sessionToken}` },
        }
      )
      .then((response) => {
        getVotePair();
      })
      .catch((error) => {
        console.log(error);
        setScreenIsLoading(false);
      });
  };
  const regularVoteHandler = async (
    _id: string,
    selected: string,
    deck: boolean
  ) => {
    setScreenIsLoading(true);
    await axios
      .patch(
        `${API_URL}/votes/battle/${_id}`,
        {
          selectedArtworkID: selected,
          deck: deck,
        },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${user.sessionToken}` },
        }
      )
      .then((response) => {
        getVotePair();
      })
      .catch((error) => {
        console.log(error);
        setScreenIsLoading(false);
      });
  };
  const silentVoteHandler = async (
    _id: string,
    selected: string,
    deck: boolean
  ) => {
    setScreenIsLoading(true);
    await axios
      .patch(
        `${API_URL}/votes/silent/${_id}`,
        {
          selectedArtworkID: selected,
          deck: deck,
        },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${user.sessionToken}` },
        }
      )
      .then((response) => {
        getVotePair();
      })
      .catch((error) => {
        console.log(error);
        setScreenIsLoading(false);
      });
  };
  const skipRegularVoteHandler = async (_id: string) => {
    setScreenIsLoading(true);
    await axios
      .delete(`${API_URL}/votes/battle/${_id}`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${user.sessionToken}` },
      })
      .then((response) => {
        getVotePair();
      })
      .catch((error) => {
        console.log(error);
        setScreenIsLoading(false);
      });
  };
  const skipSilentVoteHandler = async (_id: string) => {
    setScreenIsLoading(true);
    await axios
      .delete(`${API_URL}/votes/silent/${_id}`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${user.sessionToken}` },
      })
      .then((response) => {
        getVotePair();
      })
      .catch((error) => {
        console.log(error);
        setScreenIsLoading(false);
      });
  };

  const reportHandler = async (_id: string) => {
    await axios.post(
      `${API_URL}/reports/`,
      {
        artworkID: _id,
      },
      {
        withCredentials: true,
        headers: { Authorization: `Bearer ${user.sessionToken}` },
      }
    );
  };

  useEffect(() => {
    console.log("vote use effect");
    getVotePair();
  }, []);

  useEffect(() => {
    if (vote || silentVote) {
      setScreenIsLoading(true);
      let loadedImages = 0;
      const checkImagesLoaded = () => {
        console.log("loaded images", loadedImages);
        if (loadedImages === 2) {
          setScreenIsLoading(false);
        }
      };

      const img1 = new Image();
      img1.onload = () => {
        loadedImages++;
        checkImagesLoaded();
      };
      img1.src = vote
        ? vote.artwork1Src
        : silentVote
        ? silentVote.artwork1Src
        : "";
      const img2 = new Image();
      img2.onload = () => {
        loadedImages++;
        checkImagesLoaded();
      };
      img2.src = vote
        ? vote.artwork2Src
        : silentVote
        ? silentVote.artwork2Src
        : "";
    }
  }, [vote, silentVote]);

  if (screenIsLoading) {
    return <GlobalAppLoading text={t.loadingText} />;
  }

  if (isBlocked) {
    return (
      <VoteBlocked serverTime={serverTime} isBlockedDate={user.isBlockedDate} />
    );
  }

  if (captcha) {
    return (
      <VoteCaptcha
        {...captcha}
        voteCaptchaHandler={voteCaptchaHandler}
        balance={balance}
      />
    );
  }

  if (vote) {
    return (
      <RegularVote
        {...vote}
        voteTipViewed={voteTipViewed}
        setVoteTipViewed={setVoteTipViewed}
        regularVoteHandler={regularVoteHandler}
        skipVoteHandler={skipRegularVoteHandler}
        reportHandler={reportHandler}
        balance={balance}
      />
    );
  }

  if (silentVote) {
    return (
      <SilentVote
        {...silentVote}
        voteTipViewed={voteTipViewed}
        setVoteTipViewed={setVoteTipViewed}
        silentVoteHandler={silentVoteHandler}
        skipVoteHandler={skipSilentVoteHandler}
        reportHandler={reportHandler}
        balance={balance}
      />
    );
  }

  return <VoteEmpty />;
};

export default Vote;
